import classNames from "classnames";
import React, { useEffect, useId, useState } from "react";
import style from "../../scss/export.module.scss";
import AnimateHeight from "../AnimateHeight";
import { ChevronDownIcon } from "../Icons";
import "./accordion.scss";

export type TAccordion = {
    title: string | JSX.Element;
    titleOnExpanded?: string | null;
    children: React.ReactNode;
    initiallyExpanded?: boolean;
    contentAbove?: boolean;
    stripped?: boolean;
    btnBgColor?: string;
    btnTextColor?: string;
    onToggle?: (arg0: boolean) => void;
    collapsedChildrenInDom?: boolean;
    className?: string;
    forceExpand?: boolean;
    forceCollapse?: boolean;
};

export const Accordion = ({
    title,
    titleOnExpanded = null,
    children,
    initiallyExpanded = false,
    contentAbove = false,
    stripped = false,
    btnTextColor = style.blackColor,
    btnBgColor = style.whiteColor,
    onToggle,
    collapsedChildrenInDom = true,
    className = "",
    forceExpand = false,
    forceCollapse = false,
}: TAccordion) => {
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const buttonId = useId();
    const panelId = useId();

    const handleOnClick = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        onToggle && onToggle(expanded);
        //eslint-disable-next-line
    }, [expanded]);

    useEffect(() => {
        if (forceExpand) {
            setExpanded(true);
        }
    }, [forceExpand]);

    useEffect(() => {
        if (forceCollapse) {
            setExpanded(false);
        }
    }, [forceCollapse]);

    const btnStyle = {
        backgroundColor: stripped ? "transparent" : btnBgColor,
        color: btnTextColor,
    };

    const accordionTitle = expanded && titleOnExpanded !== null ? titleOnExpanded : title;

    return (
        <section
            className={classNames("c-accordion", className, {
                "c-accordion--expanded": expanded,
                "c-accordion--content-above": contentAbove,
                "c-accordion--stripped": stripped,
            })}
        >
            <h2 className="c-accordion__heading">
                <button
                    id={buttonId}
                    className="c-accordion__btn"
                    onClick={handleOnClick}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    style={btnStyle}
                >
                    <div aria-hidden>
                        <ChevronDownIcon className="c-accordion__icon" color={btnTextColor} />
                    </div>
                    {accordionTitle}
                </button>
            </h2>
            <AnimateHeight
                animateOpacity
                id={panelId}
                duration={200}
                height={expanded ? "auto" : 0}
                aria-hidden={!expanded}
                aria-labelledby={buttonId}
            >
                {collapsedChildrenInDom || (!collapsedChildrenInDom && expanded) ? children : null}
            </AnimateHeight>
        </section>
    );
};

export default Accordion;
